import { App, Flex, Form } from "antd";
import { FC, useCallback, useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
    getApiError,
    getAreaInfo,
    getBuildingInfoByAddress,
    getPointInfoThunk,
    getPointListByAddressId,
    patchMarketV2ChangeBuilding,
    postBuildingCreate,
} from "tm-shared/api";
import { MarketV2, Option } from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";

import { API_ERROR_CODE, ROUTES } from "@/constants";
import { AddAddressModal, DuplicateByAddressModal } from "@/domains/point";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { PageBlock } from "@/shared/pageComposition/BlockList";
import { BaseButton, PrimaryText, SecondaryText } from "@/shared/ui";
import {
    setAddressVerifyDuplicateResolution,
    setAddressVerifyModalSelectionPayload,
} from "@/store/slices/ui";
import { AddressVerifyFormValues } from "@/types/form";
import {
    AddAddressModalPayload,
    DuplicateByAddressModalPayload,
} from "@/types/modals";

import { AddressSection } from "./AddressSection";
import { MarketVerifyAddressCapabilities } from "./MarketVerifyAddress.policy";
import { StyledFormWrapper } from "./Styled";

export interface MarketVerifyAddressModel {
    capabilities: MarketVerifyAddressCapabilities;
    market: MarketV2;
    pointId: string;
}

type ModalState =
    | {
          type: "addAddress";
          payload: AddAddressModalPayload;
      }
    | {
          type: "duplicateByAddress";
          payload: DuplicateByAddressModalPayload;
      }
    | null;

interface VerifyAddressFormBlockProps {
    market: MarketV2;
    pointId: string;
}

export const VerifyAddressFormBlock: FC<VerifyAddressFormBlockProps> = ({
    market,
    pointId,
}) => {
    const { notification } = App.useApp();

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {
        addressVerifyModalSelectionPayload,
        addressVerifyDuplicateResolution,
    } = useAppSelector((state) => state.ui);

    const [optionCache, setOptionCache] = useState<Option[]>([]);
    const [modal, setModal] = useState<ModalState>(null);
    const [regionLabel, setRegionLabel] = useState("");

    const regionId = market.building.address.areaId;

    const addressVerifyForm = useForm<AddressVerifyFormValues>({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: {
            address: "",
        },
    });

    const {
        control,
        setValue,
        handleSubmit,
        formState: { isSubmitting },
    } = addressVerifyForm;

    const address = useWatch<AddressVerifyFormValues>({
        name: "address",
        control,
    });

    const onSubmit = useCallback(
        async (data: AddressVerifyFormValues) => {
            const { address } = data;

            const resolvedParentId = addressVerifyDuplicateResolution?.parentId;

            if (addressVerifyDuplicateResolution?.confirmed !== true) {
                const currentOption = optionCache.find(
                    (option) => option.value === address,
                );

                try {
                    const { result: pointsFromServer } =
                        await getPointListByAddressId({
                            addressId: address,
                        });

                    if (pointsFromServer.length > 0) {
                        setModal({
                            type: "duplicateByAddress",
                            payload: {
                                points: pointsFromServer,
                                address: currentOption?.label ?? "",
                                variant: "verifyAddress",
                            },
                        });

                        return;
                    }
                } catch (error) {
                    const { displayMessage } = getApiError(error);

                    notification.error({
                        title: "Помилка при перевірці адреси",
                        description: displayMessage,
                    });

                    return;
                }
            }

            let buildingId = null;

            try {
                const { result: building } = await getBuildingInfoByAddress({
                    addressId: address,
                });

                buildingId = building.id;
            } catch (error) {
                const { code } = getApiError(error);

                if (code === API_ERROR_CODE.NOT_FOUND) {
                    const { result: createdBuildingId } =
                        await postBuildingCreate({
                            addressId: address,
                        });

                    buildingId = createdBuildingId;
                } else {
                    throw error;
                }
            }

            try {
                await patchMarketV2ChangeBuilding({
                    marketId: market.id,
                    parentId: resolvedParentId,
                    buildingId,
                });
            } catch (error) {
                const { displayMessage } = getApiError(error);

                notification.error({
                    title: "Помилка при зміні адреси",
                    description: displayMessage,
                });

                return;
            }

            await dispatch(getPointInfoThunk({ pointId }));
            dispatch(setAddressVerifyDuplicateResolution(null));

            navigate(buildRoutePath(ROUTES.PointDetails, { pointId }), {
                replace: true,
            });
        },
        [
            addressVerifyDuplicateResolution?.parentId,
            addressVerifyDuplicateResolution?.confirmed,
            market.id,
            dispatch,
            pointId,
            navigate,
            optionCache,
            notification,
        ],
    );

    const handleCloseModal = useCallback(() => {
        setModal(null);
    }, []);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const { result } = await getAreaInfo({ area: regionId });
                if (!cancelled) setRegionLabel(result.name);
            } catch (error) {
                if (!cancelled) setRegionLabel("");
            }
        };

        void load();

        return () => {
            cancelled = true;
        };
    }, [regionId]);

    useEffect(() => {
        if (addressVerifyModalSelectionPayload) {
            const { fieldName, option, value } =
                addressVerifyModalSelectionPayload;

            setValue(fieldName, value);

            if (option) {
                setOptionCache([option]);
            }

            dispatch(setAddressVerifyDuplicateResolution(null));
            dispatch(setAddressVerifyModalSelectionPayload(null));
        }
    }, [dispatch, addressVerifyModalSelectionPayload, setValue]);

    useEffect(() => {
        if (addressVerifyDuplicateResolution?.confirmed === true) {
            void handleSubmit(onSubmit)();
        }
    }, [addressVerifyDuplicateResolution?.confirmed, handleSubmit, onSubmit]);

    const onOpenAddAddressModal = useCallback(() => {
        setModal({
            type: "addAddress",
            payload: {
                region: regionLabel,
                regionId,
                variant: "verifyAddress",
            },
        });
    }, [regionId, regionLabel]);

    const currentAddressLabel =
        optionCache.find((option) => option.value === address)?.label ?? "";

    return (
        <>
            <FormProvider {...addressVerifyForm}>
                <StyledFormWrapper>
                    <Form
                        layout="vertical"
                        onFinish={handleSubmit(onSubmit)}
                        disabled={isSubmitting}
                        style={{ width: "100%" }}
                    >
                        <Flex vertical gap={4}>
                            <SecondaryText>
                                Не верифікована адреса:
                            </SecondaryText>
                            <PrimaryText>
                                {market.building.address.name}
                            </PrimaryText>
                        </Flex>

                        <AddressSection
                            onOpenAddAddressModal={onOpenAddAddressModal}
                            address={address}
                            regionId={regionId}
                            options={optionCache}
                            setOptions={setOptionCache}
                        />

                        {currentAddressLabel && (
                            <Flex vertical gap={4}>
                                <PrimaryText>Повна адреса</PrimaryText>
                                <PrimaryText fontWeight="500">
                                    {currentAddressLabel}
                                </PrimaryText>
                            </Flex>
                        )}

                        <Flex style={{ marginTop: "auto" }}>
                            <BaseButton
                                size="large"
                                type="primary"
                                htmlType="submit"
                                loading={isSubmitting}
                            >
                                Зберегти
                            </BaseButton>
                        </Flex>
                    </Form>
                </StyledFormWrapper>
            </FormProvider>

            {modal?.type === "addAddress" && (
                <AddAddressModal
                    onClose={handleCloseModal}
                    payload={modal.payload}
                />
            )}
            {modal?.type === "duplicateByAddress" && (
                <DuplicateByAddressModal
                    onClose={handleCloseModal}
                    payload={modal.payload}
                />
            )}
        </>
    );
};

export const getMarketVerifyAddressBlocks = (
    model: MarketVerifyAddressModel,
): PageBlock<MarketVerifyAddressModel>[] => [
    {
        key: "verify-address-form",
        order: 10,
        isVisible: (model) => model.capabilities.canVerifyAddress,
        render: () => (
            <VerifyAddressFormBlock
                market={model.market}
                pointId={model.pointId}
            />
        ),
    },
];
